import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountsApi, sellersApi, adminApi, authApi, clearAuthFlag, hasAuthFlag } from '../lib/api';
import { SITE } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Package, Users, LogOut, Plus, Pencil, Trash2, Upload, Sparkles,
  Star, ExternalLink, Loader2, Image as ImageIcon, X, Link2,
} from 'lucide-react';

const emptyAccount = {
  title: '', price: 0, image: '', gallery: [], server: 'Indonesia (ID)',
  type: 'Accounts', category: 'accounts', description: '',
  details: { 'Account Level': '', Vault: '', Rank: '', Bundles: '' },
  seller: { name: '', rating: 5, reviews: 0, avatar: '', online: true },
};
const emptySeller = { name: '', rating: 5, reviews: 0, avatar: '', online: true };

const AccountsList = ({ loading, accounts, onEdit, onDelete }) => {
  if (loading) {
    return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-300" /></div>;
  }
  if (accounts.length === 0) {
    return (
      <div className="bg-[#111b38] border border-dashed border-white/10 rounded-xl p-12 text-center">
        <Package className="h-12 w-12 mx-auto text-slate-500 mb-3" />
        <p className="text-slate-400">No accounts yet. Create one or seed demo data.</p>
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {accounts.map((a) => (
        <div key={a.id} className="bg-[#111b38] border border-white/10 rounded-xl p-3 flex items-center gap-4">
          <img src={a.image} alt="" className="h-16 w-16 object-cover rounded-lg bg-[#0b1530]" />
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium truncate">{a.title}</div>
            <div className="text-xs text-slate-400 mt-1">
              {a.seller?.name} • {a.server} • <span className="price-glow font-bold">${a.price?.toFixed(2)}</span>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => onEdit(a)} className="text-slate-200 hover:bg-white/10">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(a.id)} className="text-red-400 hover:bg-red-500/10">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ accounts: 0, sellers: 0, online: 0 });
  const [accounts, setAccounts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAcc, setEditAcc] = useState(null);
  const [editSeller, setEditSeller] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, sl] = await Promise.all([adminApi.stats(), accountsApi.list(), sellersApi.list()]);
      setStats(s.data); setAccounts(a.data); setSellers(sl.data);
    } catch (err) {
      if (err.response?.status === 401) { clearAuthFlag(); navigate('/admin/login'); }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!hasAuthFlag()) { navigate('/admin/login'); return; }
    refresh();
  }, [navigate, refresh]);

  const handleLogout = async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    clearAuthFlag();
    navigate('/admin/login');
  };

  const handleSeed = async () => {
    if (!window.confirm('This will REPLACE all existing accounts & sellers with demo data. Continue?')) return;
    setSeeding(true);
    try {
      const { data } = await adminApi.seed();
      toast.success(`Seeded ${data.seeded_accounts} accounts & ${data.seeded_sellers} sellers`);
      refresh();
    } catch { toast.error('Failed to seed'); } finally { setSeeding(false); }
  };

  const handleDeleteAcc = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    await accountsApi.remove(id); toast.success('Account deleted'); refresh();
  };

  const handleDeleteSeller = async (id) => {
    if (!window.confirm('Delete this seller?')) return;
    await sellersApi.remove(id); toast.success('Seller deleted'); refresh();
  };

  const handleToggleOnline = async (seller) => {
    await sellersApi.toggleOnline(seller.id, !seller.online);
    toast.success(`${seller.name} is now ${!seller.online ? 'online' : 'offline'}`);
    refresh();
  };

  return (
    <div className="bg-marketplace min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a1428cc] border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-16 flex items-center gap-4">
          <img src={SITE.logo} alt={SITE.name} className="h-10 w-10 object-contain" />
          <div>
            <div className="font-bold bg-gradient-to-r from-amber-200 to-yellow-400 bg-clip-text text-transparent">{SITE.name}</div>
            <div className="text-[11px] text-slate-400 -mt-0.5">Admin Panel</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" onClick={() => window.open('/', '_blank')} className="text-slate-200 hover:bg-white/10 hover:text-white">
              <ExternalLink className="h-4 w-4 mr-2" /> View Store
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-slate-200 hover:bg-white/10 hover:text-white">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Accounts', value: stats.accounts, icon: Package, color: 'text-amber-300' },
            { label: 'Total Sellers', value: stats.sellers, icon: Users, color: 'text-sky-400' },
            { label: 'Online Listings', value: stats.online, icon: Sparkles, color: 'text-emerald-400' },
            { label: 'Seed Demo Data', value: 'One click', icon: Upload, color: 'text-fuchsia-400', action: handleSeed },
          ].map((s) => (
            <div key={s.label} onClick={s.action} className={`bg-[#111b38] border border-white/10 rounded-xl p-5 ${s.action ? 'cursor-pointer hover:border-amber-400/40 transition-colors' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-400">{s.label}</div>
                  <div className="text-2xl font-bold text-white mt-1">
                    {s.action && seeding ? <Loader2 className="h-6 w-6 animate-spin" /> : s.value}
                  </div>
                </div>
                <s.icon className={`h-8 w-8 ${s.color}`} />
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="accounts" className="w-full">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="accounts" className="data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900">Accounts</TabsTrigger>
            <TabsTrigger value="sellers" className="data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900">Sellers</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Account Listings ({accounts.length})</h2>
              <Button onClick={() => setEditAcc({ ...emptyAccount })} className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
                <Plus className="h-4 w-4 mr-2" /> New Account
              </Button>
            </div>
            <AccountsList
              loading={loading}
              accounts={accounts}
              onEdit={setEditAcc}
              onDelete={handleDeleteAcc}
            />
          </TabsContent>

          <TabsContent value="sellers" className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Sellers ({sellers.length})</h2>
              <Button onClick={() => setEditSeller({ ...emptySeller })} className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
                <Plus className="h-4 w-4 mr-2" /> New Seller
              </Button>
            </div>
            {sellers.length === 0 ? (
              <div className="bg-[#111b38] border border-dashed border-white/10 rounded-xl p-12 text-center">
                <Users className="h-12 w-12 mx-auto text-slate-500 mb-3" />
                <p className="text-slate-400">No sellers yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {sellers.map((s) => (
                  <div key={s.id} className="bg-[#111b38] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                    {s.avatar ? (
                      <img src={s.avatar} alt="" className="h-12 w-12 rounded-full object-cover" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-white/10 grid place-items-center text-slate-400">
                        <Users className="h-5 w-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate flex items-center gap-2">
                        {s.name}
                        {s.online && <span className="h-2 w-2 rounded-full bg-emerald-400" />}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-amber-300">
                        <Star className="h-3 w-3 fill-amber-300" /> {s.rating} • <span className="text-slate-400">({s.reviews} reviews)</span>
                      </div>
                    </div>
                    <Switch checked={s.online} onCheckedChange={() => handleToggleOnline(s)} />
                    <Button size="sm" variant="ghost" onClick={() => setEditSeller(s)} className="text-slate-200 hover:bg-white/10">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteSeller(s.id)} className="text-red-400 hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {editAcc && <AccountFormDialog account={editAcc} sellers={sellers} onClose={() => setEditAcc(null)} onSaved={refresh} />}
      {editSeller && <SellerFormDialog seller={editSeller} onClose={() => setEditSeller(null)} onSaved={refresh} />}
    </div>
  );
};

// --- Sub components ---
const ImageInput = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const { data } = await adminApi.upload(f);
      onChange(data.url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };
  return (
    <div>
      <Label className="text-slate-300">{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL" className="bg-white/5 border-white/10 text-white" />
        <label className="h-10 px-3 rounded-md bg-white/10 border border-white/10 hover:bg-white/20 cursor-pointer flex items-center gap-2 text-sm text-white shrink-0">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Upload
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </label>
      </div>
      {value && (
        <div className="mt-2 h-20 w-20 rounded-lg overflow-hidden bg-[#0b1530] border border-white/10">
          <img src={value} alt="" className="h-full w-full object-cover" />
        </div>
      )}
    </div>
  );
};

const GalleryEditor = ({ value, onChange, max = 40 }) => {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const items = value || [];
  const remaining = max - items.length;

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (items.includes(u)) { toast.error('Image already added'); return; }
    if (items.length >= max) { toast.error(`Max ${max} images`); return; }
    onChange([...items, u]);
    setUrlInput('');
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = max - items.length;
    if (room <= 0) { toast.error(`Max ${max} images`); return; }
    const toUpload = files.slice(0, room);
    if (files.length > room) {
      toast.warning(`Only uploading first ${room} (limit ${max})`);
    }
    setUploading(true);
    const newItems = [...items];
    let success = 0, failed = 0;
    for (const f of toUpload) {
      try {
        const { data } = await adminApi.upload(f);
        newItems.push(data.url);
        success++;
      } catch {
        failed++;
      }
    }
    onChange(newItems);
    setUploading(false);
    e.target.value = ''; // reset input
    if (success) toast.success(`Uploaded ${success} image${success > 1 ? 's' : ''}`);
    if (failed) toast.error(`${failed} upload${failed > 1 ? 's' : ''} failed`);
  };

  const removeAt = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-amber-300" />
          <span className="font-medium text-white">Gallery images</span>
        </div>
        <span className="text-xs text-slate-400">{items.length} / {max}</span>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUrl())}
            placeholder="Paste image URL and press Enter"
            className="pl-9 bg-white/5 border-white/10 text-white"
            disabled={remaining <= 0}
          />
        </div>
        <Button type="button" onClick={addUrl} disabled={remaining <= 0 || !urlInput.trim()}
          className="bg-white/10 hover:bg-white/20 text-white border border-white/10 h-10">
          Add
        </Button>
        <label className={`h-10 px-3 rounded-md flex items-center gap-2 text-sm shrink-0 border ${
          remaining <= 0 || uploading ? 'bg-white/5 text-slate-500 border-white/5 cursor-not-allowed'
          : 'bg-amber-400 hover:bg-amber-300 text-slate-900 border-amber-500 cursor-pointer font-semibold'
        }`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Upload
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={remaining <= 0 || uploading} />
        </label>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Add up to {max} extra photos. Max 3MB per image. You can select multiple files at once.</p>

      {items.length > 0 && (
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-[280px] overflow-y-auto scrollbar-slim pr-1">
          {items.map((src, i) => (
            <div key={`${src}-${i}`} className="relative group aspect-square rounded-md overflow-hidden bg-[#0b1530] border border-white/10">
              <img src={src} alt={`gallery-${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/70 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <span className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-white">{i + 1}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AccountFormDialog = ({ account, sellers, onClose, onSaved }) => {
  const [form, setForm] = useState(account);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSeller = (k, v) => setForm((f) => ({ ...f, seller: { ...f.seller, [k]: v } }));
  const isEdit = !!account.id;

  const handleSave = async () => {
    if (!form.title || !form.image || !form.seller?.name) {
      toast.error('Title, image, and seller name are required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (isEdit) await accountsApi.update(form.id, payload);
      else await accountsApi.create(payload);
      toast.success(isEdit ? 'Account updated' : 'Account created');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1a38] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-slim">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Account' : 'New Account'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-slate-300">Title</Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Price (USD)</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Server</Label>
              <Input value={form.server} onChange={(e) => set('server', e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" />
            </div>
          </div>
          <ImageInput label="Main Image" value={form.image} onChange={(v) => set('image', v)} />
          <GalleryEditor value={form.gallery || []} onChange={(v) => set('gallery', v)} max={40} />
          <div>
            <Label className="text-slate-300">Description</Label>
            <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} className="mt-1.5 bg-white/5 border-white/10 text-white" />
          </div>

          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-3"><ImageIcon className="h-4 w-4 text-amber-300" /><span className="font-medium">Seller info</span></div>
            {sellers.length > 0 && (
              <div className="mb-3">
                <Label className="text-slate-300 text-xs">Pick from existing sellers</Label>
                <select onChange={(e) => {
                  const s = sellers.find((x) => x.id === e.target.value);
                  if (s) setForm((f) => ({ ...f, seller: { name: s.name, rating: s.rating, reviews: s.reviews, avatar: s.avatar, online: s.online } }));
                }} className="mt-1.5 w-full h-10 bg-white/5 border border-white/10 rounded-md px-3 text-white text-sm">
                  <option value="">-- Select seller --</option>
                  {sellers.map((s) => <option key={s.id} value={s.id} className="bg-[#0f1a38]">{`${s.name} (${s.rating}★)`}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300 text-xs">Name</Label><Input value={form.seller?.name || ''} onChange={(e) => setSeller('name', e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-xs">Rating</Label><Input type="number" step="0.1" min="0" max="5" value={form.seller?.rating || 5} onChange={(e) => setSeller('rating', parseFloat(e.target.value))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-xs">Reviews</Label><Input type="number" value={form.seller?.reviews || 0} onChange={(e) => setSeller('reviews', parseInt(e.target.value) || 0)} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={form.seller?.online} onCheckedChange={(v) => setSeller('online', v)} /><span className="text-sm text-slate-300">Online</span></div>
            </div>
            <div className="mt-3"><ImageInput label="Avatar" value={form.seller?.avatar} onChange={(v) => setSeller('avatar', v)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-white/10">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SellerFormDialog = ({ seller, onClose, onSaved }) => {
  const [form, setForm] = useState(seller);
  const [saving, setSaving] = useState(false);
  const isEdit = !!seller.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) { toast.error('Seller name required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, rating: parseFloat(form.rating) || 5, reviews: parseInt(form.reviews) || 0, avatar: form.avatar || '', online: !!form.online };
      if (isEdit) await sellersApi.update(form.id, payload);
      else await sellersApi.create(payload);
      toast.success(isEdit ? 'Seller updated' : 'Seller created');
      onSaved(); onClose();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1a38] border-white/10 text-white">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Seller' : 'New Seller'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><Label className="text-slate-300">Name</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300">Rating</Label><Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => set('rating', e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Reviews</Label><Input type="number" value={form.reviews} onChange={(e) => set('reviews', e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
          </div>
          <ImageInput label="Avatar" value={form.avatar} onChange={(v) => set('avatar', v)} />
          <div className="flex items-center gap-2"><Switch checked={form.online} onCheckedChange={(v) => set('online', v)} /><span className="text-sm text-slate-300">Online</span></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-white/10">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
